import { memoryToGiB } from "components/utils";
import { GPUInstanceType } from "types/InstanceTypes";

/** Raw locations an instance can be deployed to, preferring the multi-location list. */
export function getInstanceLocations(
  instance: GPUInstanceType | undefined,
): string[] {
  if (!instance) return [];
  if (instance.available_locations?.length) return instance.available_locations;
  return instance.location ? [instance.location] : [];
}

/** Instance locations inside allowedLocations; null/undefined means no constraint. */
export function getInstanceLocationsMatching(
  instance: GPUInstanceType | undefined,
  allowedLocations: Set<string> | null | undefined,
): string[] {
  const locations = getInstanceLocations(instance);
  if (!allowedLocations) return locations;
  return locations.filter((location) => allowedLocations.has(location));
}

export function isDgxSpark(nameOrId?: string): boolean {
  if (!nameOrId) return false;
  const lower = nameOrId.toLowerCase();
  return lower.includes("dgx-spark") || lower.includes("dgx spark");
}

export function isDgxStation(nameOrId?: string): boolean {
  if (!nameOrId) return false;
  const lower = nameOrId.toLowerCase();
  return lower.includes("dgx-station") || lower.includes("dgx station");
}

export function isBrevCloud(nameOrId?: string): boolean {
  if (!nameOrId) return false;
  const lower = nameOrId.toLowerCase();
  return lower.includes("brevcloud");
}

/**
 * Returns the default storage size in GiB as a plain number string (e.g. "128").
 * Write paths intentionally use the legacy size that preceded byte-valued display
 * normalization. This keeps display-only byte metadata from changing create payloads.
 */
export function getDefaultStorageGiB(
  supportedStorage: Array<{ size: string; legacy_size?: string }> | undefined,
): string {
  const storage = supportedStorage?.[0];
  const size = storage?.legacy_size ?? storage?.size;
  if (size) return String(memoryToGiB(size));
  return "128";
}

export function getStorageBoundsGiB(
  instance: GPUInstanceType | null | undefined,
): { min: number | null; max: number | null } {
  const supportedStorage = instance?.supported_storage ?? [];
  const mins = supportedStorage
    .filter((s) => s.min_size)
    .map((s) => memoryToGiB(s.min_size!));
  const maxs = supportedStorage
    .filter((s) => s.max_size)
    .map((s) => memoryToGiB(s.max_size!));
  return {
    min: mins.length > 0 ? Math.min(...mins) : null,
    max: maxs.length > 0 ? Math.max(...maxs) : null,
  };
}

export function isStorageValueValid(
  value: string,
  instance: GPUInstanceType | null | undefined,
): boolean {
  if (!instance?.elastic_root_volume) return true;
  const { min, max } = getStorageBoundsGiB(instance);
  const num = Number(value);
  if (!value || Number.isNaN(num) || num <= 0) return min === null;
  if (min !== null && num < min) return false;
  if (max !== null && num > max) return false;
  return true;
}

/**
 * Parses an instance string that may contain a chip type prefix (e.g., "gpu@instance-type")
 */
export function parseInstanceType(instance: string | null) {
  if (!instance) {
    return null;
  }
  const [chipTypeOrInstanceType, instanceType] = instance.split("@");
  if (instanceType) {
    return {
      chipType: chipTypeOrInstanceType,
      instanceType: instanceType,
    };
  } else {
    return {
      instanceType: chipTypeOrInstanceType,
    };
  }
}
